import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, tap, catchError, throwError, finalize } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { OnboardingProgress, OnboardingProgressService } from './onboarding-progress.service';
import { NotificationService } from './notification.service';
import { resetSessionExpiryHandling } from '../interceptors/session-expiry';

/** Stashed so reset-password can POST email after WiseAuth v2 confirm returns no identity. */
export const PASSWORD_RESET_EMAIL_STORAGE_KEY = 'parseeasy_password_reset_email';

// ============================================================================
// INTERFACES
// ============================================================================

export interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  organizationId?: string;
  role?: string;
}

// User Lookup
export interface UserLookupRequest {
  email: string;
}

export interface UserLookupResponse {
  exists: boolean;
  has_wiseauth?: boolean;
  has_parseeasy?: boolean;
  account_deleted?: boolean;
  user_id?: string;
  email?: string;
}

// Login
export interface LoginRequest {
  email: string;
  password: string;
  remember_me?: boolean;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  requires_2fa: boolean;
  login_session_token?: string;
  otp_type?: string;
  is_profile_complete?: boolean;
  is_organization_complete?: boolean;
}

// 2FA
export interface Verify2FARequest {
  login_session_token: string;
  otp_code: string;
  remember_me?: boolean;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  is_profile_complete?: boolean;
  is_organization_complete?: boolean;
}

// Registration
export interface InitiateRegistrationRequest {
  email: string;
  first_name?: string;
  last_name?: string;
}

export interface InitiateRegistrationResponse {
  status: string;
  registration_token: string;
  message: string;
}

export interface VerifyOTPRequest {
  registration_token: string;
  otp_code: string;
}

export interface VerifyOTPResponse {
  status: string;
  password_setup_token: string;
  message: string;
}

export interface CompleteRegistrationRequest {
  password_setup_token: string;
  password: string;
}

export interface CompleteRegistrationResponse {
  user_id: string;
  email: string;
  message: string;
  access_token?: string;
  refresh_token?: string;
  token_type?: string;
  requires_organization_setup?: boolean;
  is_profile_complete?: boolean;
  is_organization_complete?: boolean;
}

// Password Reset
export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  message: string;
  status: string;
}

export interface ResetPasswordRequest {
  reset_token: string;
  new_password: string;
}

export interface ResetPasswordResponse {
  message: string;
  status: string;
}

// User Info
export interface UserResponse {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  is_active: boolean;
  is_verified: boolean;
  has_2fa_enabled: boolean;
  organization_id?: string;
  organization_subscription_status?: string;
  role: string;
  created_at: string;
  last_login_at?: string;
  is_profile_complete?: boolean;
  is_organization_complete?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private onboardingProgressService = inject(OnboardingProgressService);
  private notificationService = inject(NotificationService);

  private readonly REDIRECT_URL_KEY = 'parseeasy_redirect_url';
  private readonly FIRST_PARSE_COMPLETED_KEY = 'first_parse_completed';

  // SECURITY: Access token stored in MEMORY ONLY (not localStorage/sessionStorage)
  // This follows WiseAuth security best practices to prevent XSS attacks
  private accessToken: string | null = null;

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  // Track auth initialization status - guards should wait for this to complete
  private authInitializedSubject = new BehaviorSubject<boolean>(false);
  public authInitialized$ = this.authInitializedSubject.asObservable();

  constructor() {
    // On app initialization, try to restore session using refresh token (HttpOnly cookie)
    // This allows users to stay logged in across page refreshes
    // Use setTimeout to defer the call and avoid circular dependency with authInterceptor
    // The interceptor injects AuthService, so calling HTTP in constructor creates NG0200 error
    setTimeout(() => this.initializeAuth(), 0);
  }

  // ============================================================================
  // USER LOOKUP (Email-First Flow)
  // ============================================================================

  /**
   * Check if user exists in WiseAuth
   * This is the first step in the email-first authentication flow
   */
  checkUserExists(email: string): Observable<UserLookupResponse> {
    return this.http.post<UserLookupResponse>(`${environment.apiUrl}/auth/check-user`, { email });
  }

  requestAccountReactivation(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${environment.apiUrl}/auth/reactivate/request-otp`, { email });
  }

  confirmAccountReactivation(email: string, otpCode: string): Observable<{ message: string; restored: boolean }> {
    return this.http.post<{ message: string; restored: boolean }>(
      `${environment.apiUrl}/auth/reactivate/confirm`,
      { email, otp_code: otpCode }
    );
  }

  // ============================================================================
  // LOGIN FLOW
  // ============================================================================

  /**
   * Login with email and password
   * May return 2FA challenge if 2FA is enabled
   */
  login(email: string, password: string, rememberMe = false): Observable<LoginResponse> {
    // withCredentials: true is required to receive the HttpOnly refresh token cookie
    return this.http.post<LoginResponse>(`${environment.apiUrl}/auth/login`, {
      email,
      password,
      remember_me: rememberMe
    }, { withCredentials: true })
      .pipe(
        tap(response => {
          if (!response.requires_2fa) {
            // No 2FA required - store token and authenticate
            this.setAccessToken(response.access_token);
            this.isAuthenticatedSubject.next(true);
            resetSessionExpiryHandling();
            // Fetch user info
            this.fetchCurrentUser();
            // Check onboarding status and redirect if needed (isActiveLogin=true to force redirect)
            this.checkOnboardingStatus(response, true);
          }
          // If 2FA required, login_session_token will be used in verify2FA
        }),
        catchError(error => {
          console.error('Login error:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Verify 2FA code after login
   */
  verify2FA(sessionToken: string, otpCode: string, rememberMe = false, otpType?: string): Observable<TokenResponse> {
    // withCredentials: true is required to receive the HttpOnly refresh token cookie
    const normalizedType = (otpType || 'email').toLowerCase();
    const body: Record<string, string | boolean> = {
      login_session_token: sessionToken,
      otp_code: otpCode,
      otp: otpCode,
      remember_me: rememberMe,
      otp_type: normalizedType,
    };
    return this.http.post<TokenResponse>(`${environment.apiUrl}/auth/verify-2fa`, body, { withCredentials: true }).pipe(
      tap(response => {
        this.setAccessToken(response.access_token);
        this.isAuthenticatedSubject.next(true);
        resetSessionExpiryHandling();
        this.fetchCurrentUser();
        // Check onboarding status and redirect if needed (isActiveLogin=true to force redirect)
        this.checkOnboardingStatus(response, true);
      }),
      catchError(error => {
        console.error('2FA verification error:', error);
        return throwError(() => error);
      })
    );
  }

  // ============================================================================
  // REGISTRATION FLOW
  // ============================================================================

  /**
   * Initiate registration with OTP
   */
  initiateRegistration(email: string, firstName?: string, lastName?: string): Observable<InitiateRegistrationResponse> {
    const request: InitiateRegistrationRequest = { email };
    if (firstName) request.first_name = firstName;
    if (lastName) request.last_name = lastName;

    return this.http.post<InitiateRegistrationResponse>(`${environment.apiUrl}/auth/register/initiate`, request)
      .pipe(
        catchError(error => {
          console.error('Registration initiation error:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Verify OTP code during registration
   */
  verifyOTP(registrationToken: string, otpCode: string): Observable<VerifyOTPResponse> {
    return this.http.post<VerifyOTPResponse>(`${environment.apiUrl}/auth/register/verify-otp`, {
      registration_token: registrationToken,
      otp_code: otpCode
    }).pipe(
      catchError(error => {
        console.error('OTP verification error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Complete registration by setting password
   */
  completeRegistration(passwordSetupToken: string, password: string): Observable<CompleteRegistrationResponse> {
    return this.http.post<CompleteRegistrationResponse>(`${environment.apiUrl}/auth/register/complete`, {
      password_setup_token: passwordSetupToken,
      password: password
    }).pipe(
      tap(response => {
        if (response.access_token) {
          this.setAccessToken(response.access_token);
          this.isAuthenticatedSubject.next(true);
          this.fetchCurrentUser();
        }
      }),
      catchError(error => {
        console.error('Registration completion error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Called after organization setup completes or is skipped — playground-first home.
   */
  navigateAfterOrganizationSetup(): void {
    this.navigateToPostAuthHome();
  }

  // ============================================================================
  // PASSWORD RESET FLOW
  // ============================================================================

  /**
   * Request password reset email
   */
  forgotPassword(email: string): Observable<ForgotPasswordResponse> {
    return this.http.post<ForgotPasswordResponse>(`${environment.apiUrl}/auth/forgot-password`, { email })
      .pipe(
        catchError(error => {
          console.error('Forgot password error:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Reset password with token from email
   */
  resetPassword(token: string, newPassword: string, email?: string): Observable<ResetPasswordResponse> {
    const body: { reset_token: string; new_password: string; email?: string } = {
      reset_token: token,
      new_password: newPassword
    };
    if (email) {
      body.email = email;
    }
    return this.http.post<ResetPasswordResponse>(`${environment.apiUrl}/auth/reset-password`, body).pipe(
      catchError(error => {
        console.error('Password reset error:', error);
        return throwError(() => error);
      })
    );
  }

  validateResetToken(token: string): Observable<{ valid: boolean; reason: string; message: string }> {
    return this.http.get<{ valid: boolean; reason: string; message: string }>(
      `${environment.apiUrl}/auth/reset-password/validate`,
      { params: { token } }
    );
  }

  // ============================================================================
  // LOGOUT & TOKEN MANAGEMENT
  // ============================================================================

  /**
   * Logout user and clear session
   * IMPORTANT: Backend call must complete BEFORE navigation to clear refresh token cookie
   */
  clearLocalSession(): void {
    this.clearAppStorage();
    this.clearAccessToken();
    this.isAuthenticatedSubject.next(false);
    this.currentUserSubject.next(null);
    this.router.navigate(['/auth/login']);
  }

  logout(): Observable<any> {
    const token = this.getToken();

    // Call backend logout endpoint BEFORE clearing local state or navigating
    // The backend needs the Bearer token to identify the user and clear the refresh token cookie
    if (token) {
      return this.http.post(`${environment.apiUrl}/auth/logout`, {}, { withCredentials: true }).pipe(
        tap(() => {
          console.log('[AUTH] Backend logout successful, refresh token cookie cleared');
          this.clearLocalSession();
        }),
        catchError(error => {
          console.error('Logout error:', error);
          this.clearLocalSession();
          return of(null);
        })
      );
    }

    this.clearLocalSession();
    return of(null);
  }

  /**
   * Refresh access token using refresh token (in HttpOnly cookie)
   */
  refreshToken(): Observable<TokenResponse> {
    return this.http.post<TokenResponse>(`${environment.apiUrl}/auth/refresh`, {}, { withCredentials: true })
      .pipe(
        tap(response => {
          this.setAccessToken(response.access_token);
        }),
        catchError(error => {
          console.error('Token refresh error:', error);
          this.notifyCutoverReLogin(error);
          this.logout();
          return throwError(() => error);
        })
      );
  }

  // ============================================================================
  // USER INFO
  // ============================================================================

  /**
   * Get current user information
   */
  getCurrentUser(): Observable<UserResponse> {
    return this.http.get<UserResponse>(`${environment.apiUrl}/auth/me`)
      .pipe(
        tap(user => {
          const mappedUser: User = {
            id: user.id,
            email: user.email,
            first_name: user.first_name,
            last_name: user.last_name,
            organizationId: user.organization_id,
            role: user.role
          };
          this.currentUserSubject.next(mappedUser);
          if (this.isOrganizationSuspended(user.organization_subscription_status)) {
            this.clearLocalSession();
            this.router.navigate(['/organization-suspended']);
          }
          console.log('[AUTH] User loaded:', mappedUser);
        }),
        catchError(error => {
          console.error('Get current user error:', error);
          return throwError(() => error);
        })
      );
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Get access token from memory
   */
  getToken(): string | null {
    return this.accessToken;
  }

  isOrganizationSuspended(status?: string | null): boolean {
    const normalized = (status || '').toLowerCase();
    return normalized === 'suspended' || normalized === 'cancelled' || normalized === 'canceled';
  }

  /**
   * SECURITY: Set access token in MEMORY ONLY
   * Following WiseAuth best practices - never store in localStorage/sessionStorage
   */
  private setAccessToken(token: string): void {
    this.accessToken = token;
  }

  /**
   * Clear access token from memory
   */
  private clearAccessToken(): void {
    this.accessToken = null;
  }

  /**
   * Remove ParseEasy keys from browser storage on logout.
   */
  private clearAppStorage(): void {
    const exactKeys = [
      'onboarding_welcome_dismissed',
      'onboarding_checklist_dismissed',
      'onboarding_tour_completed',
      'parseeasy_billingonboarding_dismissed',
      'parseeasy_billing_onboarding_dismissed',
      'parseeasy_redirect_url',
      'parseeasy_ux_persona',
      'first_parse_completed',
      'credits_exhausted_modal_dismissed',
      'credits_known_exhausted',
    ];
    for (const key of exactKeys) {
      localStorage.removeItem(key);
    }
    // Preserve template drafts across logout (account-scoped keys).
    const prefixes = ['parseeasy_', 'onboarding_'];
    const preservePrefixes = ['parseeasy_template_draft'];
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (
        key &&
        prefixes.some(prefix => key.startsWith(prefix)) &&
        !preservePrefixes.some(prefix => key === prefix || key.startsWith(`${prefix}:`))
      ) {
        localStorage.removeItem(key);
      }
    }
    sessionStorage.clear();
  }

  /**
   * Fetch current user info from backend
   */
  private fetchCurrentUser(): void {
    this.getCurrentUser().subscribe({
      next: () => {
        // User info is set in getCurrentUser tap operator
      },
      error: (error) => {
        console.error('Failed to fetch user info:', error);
        if (error?.status === 401) {
          this.logout();
        }
      }
    });
  }

  /**
   * Initialize authentication on app startup
   * Attempts to restore session using refresh token (HttpOnly cookie)
   * This allows users to stay logged in across page refreshes
   */
  private initializeAuth(): void {
    console.log('[AUTH DEBUG] initializeAuth called');
    console.log('[AUTH DEBUG] Making request to:', `${environment.apiUrl}/auth/refresh`);

    // Try to refresh token using HttpOnly cookie
    this.http.post<TokenResponse>(`${environment.apiUrl}/auth/refresh`, {}, { withCredentials: true })
      .subscribe({
        next: (response) => {
          // Successfully restored session
          console.log('[AUTH DEBUG] Refresh successful, access_token received');
          this.setAccessToken(response.access_token);
          this.isAuthenticatedSubject.next(true);
          this.authInitializedSubject.next(true);
          resetSessionExpiryHandling();
          this.fetchCurrentUser();
          // Check onboarding status and redirect if needed
          this.checkOnboardingStatus(response);
          console.log('Session restored from refresh token');
        },
        error: (error) => {
          // No valid refresh token - user needs to login
          // This is normal for first-time visitors or after session expiry
          console.log('[AUTH DEBUG] Refresh failed with error:', error);
          console.log('[AUTH DEBUG] Error status:', error.status);
          console.log('[AUTH DEBUG] Error statusText:', error.statusText);
          console.log('[AUTH DEBUG] Error message:', error.message);
          this.notifyCutoverReLogin(error, { onlyIfCutoverCopy: true });
          this.isAuthenticatedSubject.next(false);
          this.authInitializedSubject.next(true);
          console.log('No valid session found - user needs to login');
        }
      });
  }

  /**
   * Set redirect URL for post-login navigation
   */
  setRedirectUrl(url: string): void {
    localStorage.setItem(this.REDIRECT_URL_KEY, url);
  }

  /**
   * Get redirect URL
   */
  getRedirectUrl(): string | null {
    return localStorage.getItem(this.REDIRECT_URL_KEY);
  }

  /**
   * Clear redirect URL
   */
  clearRedirectUrl(): void {
    localStorage.removeItem(this.REDIRECT_URL_KEY);
  }

  /**
   * Mark that the user has completed their first successful parse (activation).
   */
  markFirstParseCompleted(): void {
    localStorage.setItem(this.FIRST_PARSE_COMPLETED_KEY, 'true');
  }

  /**
   * Whether the user has completed a first parse (local activation signal).
   */
  hasCompletedFirstParse(): boolean {
    return localStorage.getItem(this.FIRST_PARSE_COMPLETED_KEY) === 'true';
  }

  /**
   * Resolve post-auth default home.
   * Prefer stored deep-link redirect; otherwise playground until activated,
   * then dashboard for returning activated users.
   */
  getPostAuthHome(progress?: OnboardingProgress | null): string {
    const redirectUrl = this.getRedirectUrl();
    if (redirectUrl) {
      return redirectUrl;
    }

    if (this.hasCompletedFirstParse()) {
      return '/dashboard';
    }

    if (progress) {
      const parseDone = progress.steps.some(
        step => (step.id === 'parse' || step.id === 'document') && step.completed
      );
      if (parseDone || progress.percent_complete >= 100) {
        if (parseDone) {
          this.markFirstParseCompleted();
        }
        return '/dashboard';
      }
    }

    return '/playground';
  }

  /**
   * Navigate to post-auth home (playground-first for incomplete onboarding).
   * Consumes and clears any stored deep-link redirect.
   */
  navigateToPostAuthHome(): void {
    const storedRedirect = this.getRedirectUrl();
    if (storedRedirect) {
      this.clearRedirectUrl();
      setTimeout(() => {
        this.router.navigateByUrl(storedRedirect);
      }, 100);
      return;
    }

    if (this.hasCompletedFirstParse()) {
      setTimeout(() => {
        this.router.navigateByUrl('/dashboard');
      }, 100);
      return;
    }

    this.onboardingProgressService.getProgress().subscribe({
      next: (progress) => {
        const home = this.getPostAuthHome(progress);
        // getPostAuthHome may have consumed redirect; clear if still set
        this.clearRedirectUrl();
        setTimeout(() => {
          this.router.navigateByUrl(home);
        }, 100);
      },
      error: () => {
        setTimeout(() => {
          this.router.navigateByUrl('/playground');
        }, 100);
      }
    });
  }

  /**
   * After active login/register, route to post-auth home.
   * Org/profile incompleteness is soft-checklist only — never hard-redirect.
   *
   * @param response - The login or token response (completion flags kept for logging)
   * @param isActiveLogin - If true, this is from an active login action (should always redirect).
   *                        If false, this is from session restoration (skip redirects).
   */
  private checkOnboardingStatus(response: LoginResponse | TokenResponse, isActiveLogin: boolean = false): void {
    const isOrgComplete = response.is_organization_complete ?? true;
    const isProfileComplete = response.is_profile_complete ?? true;
    const currentUrl = this.router.url;

    console.log('[AUTH DEBUG] checkOnboardingStatus called:', {
      isActiveLogin,
      isOrgComplete,
      isProfileComplete,
      currentUrl
    });

    // For session restoration (not active login), don't redirect
    if (!isActiveLogin) {
      if (currentUrl.includes('/auth/')) {
        console.log('[AUTH DEBUG] Session restored, user on auth page - no redirect');
        return;
      }
      console.log('[AUTH DEBUG] Session restored on protected page - no onboarding redirect');
      return;
    }

    // ACTIVE LOGIN: Never force org/profile walls — playground-first home
    console.log('[AUTH DEBUG] Active login — navigating to post-auth home (no hard gates)');
    this.navigateToPostAuthHome();
  }

  // ============================================================================
  // ORGANIZATION SETUP
  // ============================================================================

  /**
   * Complete organization setup for new users or WiseAuth users
   * This is called after registration or first login for WiseAuth users
   */
  setupOrganization(
    orgName: string,
    companySize: string,
    industry: string,
    country: string,
    ownerFirstName: string,
    ownerLastName: string
  ): Observable<any> {
    const token = this.getToken();
    const options = token ? {
      headers: { Authorization: `Bearer ${token}` }
    } : {};

    return this.http.post(`${environment.apiUrl}/auth/organization/setup`, {
      org_name: orgName,
      company_size: companySize,
      industry: industry,
      country: country,
      owner_first_name: ownerFirstName,
      owner_last_name: ownerLastName
    }, options).pipe(
      tap(response => {
        console.log('Organization setup successful:', response);
      }),
      catchError(error => {
        console.error('Organization setup error:', error);
        return throwError(() => error);
      })
    );
  }

  private notifyCutoverReLogin(
    error: { status?: number; error?: { detail?: unknown } },
    options: { onlyIfCutoverCopy?: boolean } = {},
  ): void {
    const detail = typeof error?.error?.detail === 'string' ? error.error.detail.trim() : '';
    const isCutover = detail.toLowerCase().includes('security update');
    if (options.onlyIfCutoverCopy && !isCutover) {
      return;
    }
    if (error?.status !== 401 && !isCutover) {
      return;
    }
    this.notificationService.showError(detail || 'Sign in again after a security update.');
  }
}

